import { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Input, Select, Label } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useWizardStore } from '../lib/wizard-store';
import type { ReplyBrandHandler } from '../lib/wizard-store';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type DnsStatus = 'idle' | 'pending' | 'ok' | 'failed';

interface DnsCheckResult {
  status: 'ok' | 'pending' | 'failed';
  domain: string;
  domain_id: string | null;
  ionos_forwarder_active: boolean;
  ionos_forwarder_target: string | null;
  last_check_at: string | null;
  details: string;
}

async function fetchDnsCheck(address: string): Promise<DnsCheckResult> {
  const res = await fetch(`/v1/forwarders/check?address=${encodeURIComponent(address)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`forwarders/check failed: ${res.status}`);
  return res.json() as Promise<DnsCheckResult>;
}

export function W5_ReplyForwarder() {
  const {
    reply_to_address,
    reply_to_brand_handler,
    setReplyToAddress,
    setReplyToBrandHandler,
  } = useWizardStore();

  const [dnsStatus, setDnsStatus] = useState<DnsStatus>('idle');
  const [dnsDetails, setDnsDetails] = useState<string>('');
  const [checking, setChecking] = useState(false);

  const isValidEmail = EMAIL_RE.test(reply_to_address);

  async function checkDns() {
    if (!isValidEmail) return;
    setChecking(true);
    setDnsStatus('pending');
    try {
      const result = await fetchDnsCheck(reply_to_address);
      setDnsStatus(result.status);
      setDnsDetails(result.details);
    } catch {
      setDnsStatus('failed');
      setDnsDetails('Erreur réseau lors de la vérification.');
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    setDnsStatus('idle');
  }, [reply_to_address]);

  return (
    <Card>
      <CardHeader
        title="Reply-To & forwarder"
        subtitle="Adresse de réception des replies et handler de traitement automatique."
      />
      <CardContent className="space-y-5" data-testid="w5-reply-forwarder-section">
        <div className="space-y-1" data-testid="reply-to-address-field">
          <Label htmlFor="reply-to-address">Reply-To address</Label>
          <Input
            id="reply-to-address"
            value={reply_to_address}
            onChange={(e) => setReplyToAddress(e.target.value)}
            placeholder="replies@accelflow.fr"
            data-testid="reply-to-address-input"
          />
          <p className="text-xs text-gray-500">
            Cette adresse reçoit les replies de la séquence Success.ai. Doit être configurée comme
            forwarder IONOS sur le domaine.
          </p>
        </div>

        <div className="space-y-2" data-testid="dns-check-section">
          {dnsStatus === 'idle' && (
            <div
              className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600"
              data-testid="dns-status-idle"
            >
              Entrez une adresse valide puis cliquez sur &quot;Vérifier DNS&quot;.
            </div>
          )}
          {dnsStatus === 'pending' && (
            <div
              className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700"
              data-testid="dns-status-pending"
            >
              ⏳ Vérification DNS en cours…
            </div>
          )}
          {dnsStatus === 'ok' && (
            <div
              className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700"
              data-testid="dns-status-ok"
            >
              ✓ Forwarder IONOS actif vers Dispatcher
              {dnsDetails && <span className="ml-1 text-green-600">— {dnsDetails}</span>}
            </div>
          )}
          {dnsStatus === 'failed' && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              data-testid="dns-status-failed"
            >
              ✗ Forwarder pas configuré — voir runbook IONOS
              {dnsDetails && <span className="ml-1">({dnsDetails})</span>}
            </div>
          )}

          <Button
            variant="secondary"
            onClick={checkDns}
            disabled={!isValidEmail || checking}
            data-testid="recheck-dns-btn"
          >
            <RefreshCw size={14} className={checking ? 'animate-spin' : ''} />
            {checking ? 'Vérification…' : 'Vérifier DNS'}
          </Button>
        </div>

        <hr className="border-brand-border" />

        <div className="space-y-1" data-testid="brand-handler-field">
          <Label htmlFor="reply-brand-handler">Brand handler pour les replies</Label>
          <Select
            id="reply-brand-handler"
            value={reply_to_brand_handler}
            onChange={(e) => setReplyToBrandHandler(e.target.value as ReplyBrandHandler)}
            data-testid="brand-handler-select"
          >
            <option value="dispatcher_native">
              Dispatcher natif (LLM intent classify + state machine)
            </option>
            <option value="getfishnet_r1">
              Getfishnet R1 (booking automatique RDV via mail-r1-intake)
            </option>
            <option value="bigcall_agent_validation">
              BIG.call agent validation (l&apos;agent valide manuellement)
            </option>
            <option value="manual">Manuel (notif staff, aucune automation)</option>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
